import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  useTheme,
  Menu,
  TablePagination,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Global } from '@emotion/react';
import { DPOption, dpOptionsService, DPOptionQueryParams } from '../../services/dpoptions-service';
import AddDPOptionPanel from './AddDPOptionPanel';
import EditDPOptionPanel from './EditDPOptionPanel';

const DPOptions: React.FC = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'A-Z' | 'Z-A' | 'newest' | 'oldest'>('newest');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dpOptions, setDPOptions] = useState<DPOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<DPOption | null>(null);
  const [menuRowId, setMenuRowId] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Add filter states
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [filters, setFilters] = useState({
    isPublished: 'both',
    isMandatory: 'both',
    displayAs: 'both'
  });
  const [filterLoading, setFilterLoading] = useState(false);

  const fetchDPOptions = async (queryParams?: DPOptionQueryParams) => {
    try {
      setLoading(true);
      const data = await dpOptionsService.getDPOptions(queryParams);
      setDPOptions(data.data);
      setTotalCount(data.pagination.total);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchDPOptions({
      pagination: {
        page: page + 1,
        pageSize: rowsPerPage
      },
      sortBy: sortBy
    });
  }, []);

  // Effect for sort changes
  useEffect(() => {
    fetchDPOptions({
      pagination: {
        page: page + 1,
        pageSize: rowsPerPage
      },
      sortBy: sortBy
    });
  }, [sortBy]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchDPOptions({
      pagination: {
        page: newPage + 1,
        pageSize: rowsPerPage
      },
      sortBy: sortBy
    });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchDPOptions({
      pagination: {
        page: 1,
        pageSize: newRowsPerPage
      },
      sortBy: sortBy
    });
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(dpOptions.map(option => option.id).filter((id): id is number => id !== undefined));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelect = (id: number) => {
    setSelectedItems(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleDelete = async (id: number) => {
    try {
      await dpOptionsService.deleteDPOption(id);
      setDPOptions(prev => prev.filter(option => option.id !== id));
    } catch (error) {
      console.error('Error deleting Preference:', error);
    }
  };

  const handleStatusChange = async (id: number, newStatus: boolean) => {
    try {
      await dpOptionsService.updateDPOptionStatus(id, newStatus);
      setDPOptions(prev => prev.map(option => 
        option.id === id ? { ...option, isActive: newStatus } : option
      ));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handlePublish = async (id: number) => {
    try {
      await dpOptionsService.publishDPOption(id);
      setDPOptions(prev => prev.map(option => 
        option.id === id ? { ...option, isActive: true } : option
      ));
    } catch (error) {
      console.error('Error publishing option:', error);
    }
  };

  const getStatusColor = (status: boolean): 'success' | 'default' => {
    return status ? 'success' : 'default';
  };

  const getStatusLabel = (status: boolean) => {
    return status ? 'Active' : 'Inactive';
  };

  const handleEdit = () => {
    if (selectedItems.length === 1) {
      const option = dpOptions.find(o => o.id === selectedItems[0]) || null;
      setSelectedOption(option);
      setIsEditPanelOpen(true);
    }
  };

  const handleConfirmDialog = (title: string, message: string, onConfirm: () => void) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
    });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  const handleConfirmAction = () => {
    confirmDialog.onConfirm();
    handleCloseConfirmDialog();
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (field: keyof typeof filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = async () => {
    try {
      setFilterLoading(true);
      const queryParams: DPOptionQueryParams = {
        filters: {
          isPublished: filters.isPublished === 'both' ? undefined : filters.isPublished === 'published',
          isMandatory: filters.isMandatory === 'both' ? undefined : filters.isMandatory === 'yes',
          displayAs: filters.displayAs === 'both' ? undefined : 
            filters.displayAs === 'button' ? 'BUTTON' : 'CHECKBOX'
        },
        pagination: {
          page: page + 1,
          pageSize: rowsPerPage
        },
        sortBy: sortBy
      };

      await fetchDPOptions(queryParams);
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      setFilterLoading(false);
      handleFilterClose();
    }
  };

  const hasActiveFilters = () => {
    return filters.isPublished !== 'both' || 
           filters.isMandatory !== 'both' || 
           filters.displayAs !== 'both';
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        color: theme.palette.text.primary
      }}>
        Loading preferences...
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.background.default }}>
      <Typography variant="h4" sx={{ 
        mb: theme.custom.titleSpacing.marginBottom, 
        color: theme.palette.text.primary 
      }}>
        Preferences
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Removed selected items actions */}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Global styles={`
            @keyframes spin {
              100% { transform: rotate(360deg); }
            }
          `} />
          <IconButton
            onClick={() => fetchDPOptions({
              pagination: {
                page: page + 1,
                pageSize: rowsPerPage
              },
              sortBy: sortBy
            })}
            sx={{ color: theme.palette.text.primary }}
            disabled={loading}
          >
            <RefreshIcon
              sx={{
                transition: 'transform 0.3s',
                animation: loading ? 'spin 1s linear infinite' : undefined
              }}
            />
          </IconButton>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'A-Z' | 'Z-A' | 'newest' | 'oldest')}
            size="small"
            sx={{ 
              width: 150,
              bgcolor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.divider,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '& .MuiSelect-icon': {
                color: theme.palette.text.secondary,
              },
            }}
          >
            <MenuItem value="A-Z" sx={{ color: theme.palette.text.primary }}>A-Z</MenuItem>
            <MenuItem value="Z-A" sx={{ color: theme.palette.text.primary }}>Z-A</MenuItem>
            <MenuItem value="newest" sx={{ color: theme.palette.text.primary }}>Newest</MenuItem>
            <MenuItem value="oldest" sx={{ color: theme.palette.text.primary }}>Oldest</MenuItem>
          </Select>
          <IconButton 
            onClick={handleFilterClick}
            sx={{ 
              color: hasActiveFilters() ? theme.palette.primary.main : theme.palette.text.primary,
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
              position: 'relative',
              '&::after': hasActiveFilters() ? {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: theme.palette.primary.main,
                border: `2px solid ${theme.palette.background.paper}`,
              } : {}
            }}
          >
            <FilterListIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsAddPanelOpen(true)}
            sx={{ 
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': { 
                bgcolor: theme.palette.primary.dark 
              },
              '& .MuiSvgIcon-root': {
                color: theme.palette.primary.contrastText,
              }
            }}
          >
            ADD NEW
          </Button>
        </Box>
      </Box>

      {/* Add Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 320,
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
            Filters
          </Typography>
          
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
              Published Status
            </Typography>
            <RadioGroup
              value={filters.isPublished}
              onChange={(e) => handleFilterChange('isPublished', e.target.value)}
              row
            >
              <FormControlLabel 
                value="both" 
                control={<Radio size="small" />} 
                label="Both" 
                sx={{ color: theme.palette.text.primary }}
              />
              <FormControlLabel 
                value="published" 
                control={<Radio size="small" />} 
                label="Published" 
                sx={{ color: theme.palette.text.primary }}
              />
              <FormControlLabel 
                value="draft" 
                control={<Radio size="small" />} 
                label="Draft" 
                sx={{ color: theme.palette.text.primary }}
              />
            </RadioGroup>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
              Mandatory Status
            </Typography>
            <RadioGroup
              value={filters.isMandatory}
              onChange={(e) => handleFilterChange('isMandatory', e.target.value)}
              row
            >
              <FormControlLabel 
                value="both" 
                control={<Radio size="small" />} 
                label="Both" 
                sx={{ color: theme.palette.text.primary }}
              />
              <FormControlLabel 
                value="yes" 
                control={<Radio size="small" />} 
                label="Yes" 
                sx={{ color: theme.palette.text.primary }}
              />
              <FormControlLabel 
                value="no" 
                control={<Radio size="small" />} 
                label="No" 
                sx={{ color: theme.palette.text.primary }}
              />
            </RadioGroup>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
              Display As
            </Typography>
            <RadioGroup
              value={filters.displayAs}
              onChange={(e) => handleFilterChange('displayAs', e.target.value)}
              row
            >
              <FormControlLabel 
                value="both" 
                control={<Radio size="small" />} 
                label="Both" 
                sx={{ color: theme.palette.text.primary }}
              />
              <FormControlLabel 
                value="button" 
                control={<Radio size="small" />} 
                label="Radio Button" 
                sx={{ color: theme.palette.text.primary }}
              />
              <FormControlLabel 
                value="checkbox" 
                control={<Radio size="small" />} 
                label="Checkbox" 
                sx={{ color: theme.palette.text.primary }}
              />
            </RadioGroup>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
            <Button
              onClick={handleFilterClose}
              disabled={filterLoading}
              sx={{ 
                color: theme.palette.text.primary,
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={filterLoading}
              sx={{ 
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': { 
                  bgcolor: theme.palette.primary.dark 
                },
                minWidth: 100,
                position: 'relative'
              }}
            >
              {filterLoading ? (
                <CircularProgress
                  size={24}
                  sx={{
                    color: theme.palette.primary.contrastText,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                  }}
                />
              ) : (
                'Apply'
              )}
            </Button>
          </Box>
        </Box>
      </Popover>

      <TableContainer 
        component={Paper}
        sx={{
          bgcolor: theme.palette.background.paper,
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0,
          }
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: theme.palette.text.primary }}>Name</TableCell>
              <TableCell align="center" sx={{ color: theme.palette.text.primary }}>Mandatory</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Display As</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Default Selection</TableCell>
              <TableCell align="center" sx={{ color: theme.palette.text.primary }}>Allow Multiple</TableCell>
              <TableCell align="center" sx={{ color: theme.palette.text.primary }}>Published</TableCell>
              <TableCell align="center" sx={{ color: theme.palette.text.primary }}>Choices</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Created Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dpOptions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ borderBottom: 'none' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 3,
                        color: theme.palette.text.secondary,
                        textAlign: 'center'
                      }}
                    >
                      There are no preferences to display. Click the button below to create your first preference
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setIsAddPanelOpen(true)}
                      sx={{ 
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': { 
                          bgcolor: theme.palette.primary.dark 
                        },
                        '& .MuiSvgIcon-root': {
                          color: theme.palette.primary.contrastText,
                        }
                      }}
                    >
                      ADD NEW
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              dpOptions.map((option) => (
                <TableRow
                  key={option.id}
                  hover
                  sx={{
                    '&.MuiTableRow-root:hover': {
                      bgcolor: theme.palette.action.hover,
                    }
                  }}
                >
                  <TableCell 
                    sx={{ 
                      color: theme.palette.text.primary,
                      cursor: 'pointer',
                      '&:hover': {
                        color: theme.palette.primary.main,
                        textDecoration: 'underline'
                      }
                    }}
                    onClick={() => {
                      setSelectedOption(option);
                      setIsEditPanelOpen(true);
                    }}
                  >
                    {option.name}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={option.isMandatory ? 'Yes' : 'No'}
                      size="small"
                      color={option.isMandatory ? 'success' : 'default'}
                      sx={{
                        '& .MuiChip-label': {
                          color: theme.palette.getContrastText(
                            option.isMandatory 
                              ? theme.palette.success.main 
                              : theme.palette.grey[500]
                          ),
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>
                    {option.displayAs}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>
                    {option.setADefaultSelection ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={option.allowMultipleSelection ? 'Yes' : 'No'}
                      size="small"
                      color={option.allowMultipleSelection ? 'success' : 'default'}
                      sx={{
                        '& .MuiChip-label': {
                          color: theme.palette.getContrastText(
                            option.allowMultipleSelection 
                              ? theme.palette.success.main 
                              : theme.palette.grey[500]
                          ),
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={option.isPublished ? 'Published' : 'Draft'}
                      size="small"
                      color={option.isPublished ? 'success' : 'default'}
                      sx={{
                        '& .MuiChip-label': {
                          color: theme.palette.getContrastText(
                            option.isPublished 
                              ? theme.palette.success.main 
                              : theme.palette.grey[500]
                          ),
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={
                      <Box>
                        {option.options?.map((opt, index) => (
                          <div key={index}>{opt.optionValue}</div>
                        ))}
                      </Box>
                    }>
                      <Chip
                        label={option.options?.length || 0}
                        size="small"
                        sx={{
                          bgcolor: theme.palette.neutral.main,
                          color: theme.palette.neutral.contrastText
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>
                    {option.createdDate ? new Date(option.createdDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: theme.palette.text.primary,
            bgcolor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            '& .MuiTablePagination-select': {
              color: theme.palette.text.primary,
            },
            '& .MuiTablePagination-selectIcon': {
              color: theme.palette.text.primary,
            },
            '& .MuiTablePagination-displayedRows': {
              color: theme.palette.text.primary,
            },
            '& .MuiIconButton-root': {
              color: theme.palette.text.primary,
              '&.Mui-disabled': {
                color: theme.palette.action.disabled,
              },
            },
          }}
        />
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        sx={{
          '& .MuiPaper-root': {
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        {(() => {
          const option = dpOptions.find(o => o.id === menuRowId);
          if (!option) return null;
          return [
            <MenuItem
              key="edit"
              onClick={() => {
                setSelectedOption(option);
                setIsEditPanelOpen(true);
                setAnchorEl(null);
                setMenuRowId(null);
              }}
              sx={{ 
                color: theme.palette.text.primary,
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                }
              }}
            >
              Edit
            </MenuItem>,
            option.isActive === true ? (
              <MenuItem 
                key="delete" 
                onClick={() => {
                  handleConfirmDialog(
                    'Delete Item',
                    'Are you sure you want to mark this item as inactive?',
                    () => handleStatusChange(option.id, false)
                  );
                  setAnchorEl(null);
                }}
                sx={{ 
                  color: theme.palette.text.primary,
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                  }
                }}
              >
                Delete
              </MenuItem>
            ) : (
              <MenuItem 
                key="undelete" 
                onClick={() => {
                  handleConfirmDialog(
                    'Undelete Item',
                    'Are you sure you want to mark this item as active?',
                    () => handleStatusChange(option.id, true)
                  );
                  setAnchorEl(null);
                }}
                sx={{ 
                  color: theme.palette.text.primary,
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                  }
                }}
              >
                Undelete
              </MenuItem>
            )
          ];
        })()}
      </Menu>

      <AddDPOptionPanel 
        open={isAddPanelOpen}
        onClose={() => setIsAddPanelOpen(false)}
        onSave={() => fetchDPOptions({
          pagination: {
            page: page + 1,
            pageSize: rowsPerPage
          },
          sortBy: sortBy
        })}
      />

      <EditDPOptionPanel
        open={isEditPanelOpen}
        onClose={() => setIsEditPanelOpen(false)}
        option={selectedOption}
        onSave={() => fetchDPOptions({
          filters: {
            isPublished: filters.isPublished === 'both' ? undefined : filters.isPublished === 'published',
            isMandatory: filters.isMandatory === 'both' ? undefined : filters.isMandatory === 'yes',
            displayAs: filters.displayAs === 'both' ? undefined : 
              filters.displayAs === 'button' ? 'BUTTON' : 'CHECKBOX'
          },
          pagination: {
            page: page + 1,
            pageSize: rowsPerPage
          },
          sortBy: sortBy
        })}
      />

      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} sx={{ color: theme.palette.text.primary }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DPOptions; 